import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AdminPageHeader, Field, Select, Toggle, TextArea } from "./_helpers";
import MediaPicker from "@/components/admin/MediaPicker";
import { toast } from "sonner";
import { Plus, X, Search, ArrowUp, ArrowDown, Pencil, Trash2, AlertTriangle, CalendarClock, Eye, EyeOff } from "lucide-react";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
    .replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

const emptyForm = {
  title: "", spot: "", content: "", slug: "", cover_image: "",
  gallery: [],
  province_id: "", category_id: "",
  is_in_slider: false, slider_priority: 0,
  is_featured: false, is_breaking: false, is_active: true,
  publish_at: "", expire_at: "",
  // Admin-only source attribution (optional). Not shown to the public.
  source: "", source_url: "",
  // SEO
  seo_title: "", seo_description: "", seo_keywords: "",
  seo_canonical: "", seo_focus_keyword: "", seo_noindex: false,
  share_image: "",
};

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [editing, setEditing] = useState(null); // id or null when adding new
  const [open, setOpen] = useState(false);      // panel open
  const [f, setF] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [filterProvince, setFilterProvince] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [globalSearch, setGlobalSearch] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, title}
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]); // ids selected for bulk actions
  const [bulkDateOpen, setBulkDateOpen] = useState(false);
  const [bulkPublishAt, setBulkPublishAt] = useState("");
  const [bulkClearExpire, setBulkClearExpire] = useState(true);

  const PAGE_SIZE = 200;

  const load = () => {
    const params = new URLSearchParams({
      admin: "true",
      limit: String(PAGE_SIZE),
      skip: String(page * PAGE_SIZE),
      include_archived: String(showArchived),
      include_deleted: String(showArchived || showDeleted),
      only_deleted: String(showDeleted),
    });
    if (q.trim()) params.set("search", q.trim());
    // When "global search" is enabled, skip province filtering during search
    // so admins can hunt a headline across the whole network.
    if (filterProvince && !(globalSearch && q.trim())) params.set("province_id", filterProvince);
    if (filterCategory) params.set("category_id", filterCategory);
    return api.get(`/news?${params}`).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  };

  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/provinces?active_only=false").then((r) => setProvinces(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived, showDeleted, page, filterProvince, filterCategory, globalSearch]);

  // Reset to first page whenever the visible-set filters change
  useEffect(() => {
    setPage(0);
    setSelectedIds([]);
  }, [showArchived, showDeleted, filterProvince, filterCategory, globalSearch]);

  // Clear selection when the current page changes
  useEffect(() => {
    setSelectedIds([]);
  }, [page]);

  // Debounced search: reset to page 0 and refetch when the user pauses typing
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Server-side filtering handles province/category/search — items are ready to display.
  const filtered = items;

  const openNew = () => {
    setEditing(null);
    setF(emptyForm);
    setOpen(true);
  };
  const openEdit = (n) => {
    setEditing(n.id);
    setF({
      ...emptyForm,
      ...n,
      gallery: Array.isArray(n.gallery) ? n.gallery : [],
      province_id: n.province_id || "",
      category_id: n.category_id || "",
    });
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setEditing(null);
      setF(emptyForm);
    }, 200);
  };

  const save = async () => {
    if (!f.title?.trim()) return toast.error("Başlık zorunlu");
    if (!f.content?.trim()) return toast.error("İçerik zorunlu");
    setSaving(true);
    try {
      const payload = {
        ...f,
        slug: f.slug || slugify(f.title),
        province_id: f.province_id || null,
        category_id: f.category_id || null,
      };
      if (editing) await api.put(`/news/${editing}`, payload);
      else await api.post("/news", payload);
      toast.success("Kaydedildi");
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/news/${id}`);
      load();
      toast.success("Haber çöp kutusuna taşındı — 'Silinenler' bölümünden geri yükleyebilirsiniz");
    } catch {
      toast.error("Hata");
    }
  };

  const restore = async (id) => {
    try {
      await api.post(`/news/${id}/restore`);
      load();
      toast.success("Haber geri yüklendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Geri yüklenemedi");
    }
  };

  const hardRemove = async (id) => {
    if (!window.confirm("Bu haber KALICI olarak silinecek ve geri alınamayacak. Emin misiniz?")) return;
    try {
      await api.delete(`/news/${id}?hard=true`);
      load();
      toast.success("Haber kalıcı olarak silindi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi (yalnızca süper admin)");
    }
  };

  const restoreAll = async () => {
    if (!window.confirm(`Çöp kutusundaki ${total} haber geri yüklenecek. Devam edilsin mi?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/news/restore-all");
      toast.success(`${r.data.restored} haber geri yüklendi`);
      setShowDeleted(false);
      setPage(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toplu geri yükleme başarısız");
    } finally {
      setBulkBusy(false);
    }
  };

  const moveGallery = (idx, dir) => {
    const g = [...f.gallery];
    const target = idx + dir;
    if (target < 0 || target >= g.length) return;
    [g[idx], g[target]] = [g[target], g[idx]];
    setF({ ...f, gallery: g });
  };

  // ─── Bulk selection ──────────────────────────────────────────────────
  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const selectableIds = items.filter((n) => !n.deleted_at).map((n) => n.id);
  const allOnPageSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  const someOnPageSelected =
    selectableIds.some((id) => selectedIds.includes(id)) && !allOnPageSelected;
  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      // deselect all on this page
      setSelectedIds((prev) => prev.filter((id) => !selectableIds.includes(id)));
    } else {
      // add any missing selectable ids on this page
      setSelectedIds((prev) => Array.from(new Set([...prev, ...selectableIds])));
    }
  };
  const clearSelection = () => setSelectedIds([]);

  const openBulkDate = () => {
    if (selectedIds.length === 0) return toast.error("Önce en az bir haber seçin");
    // Prefill with local "now"
    const d = new Date();
    d.setSeconds(0, 0);
    const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setBulkPublishAt(iso);
    setBulkClearExpire(true);
    setBulkDateOpen(true);
  };
  const applyBulkDate = async () => {
    if (!bulkPublishAt) return toast.error("Yeni yayın tarihi seçin");
    setBulkBusy(true);
    try {
      const r = await api.post("/news/bulk-update-publish", {
        ids: selectedIds,
        publish_at: new Date(bulkPublishAt).toISOString(),
        clear_expire: bulkClearExpire,
      });
      toast.success(`${r.data.updated} haber güncellendi`);
      setBulkDateOpen(false);
      setSelectedIds([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toplu güncelleme başarısız");
    } finally {
      setBulkBusy(false);
    }
  };

  const applyBulkActive = async (nextActive) => {
    if (selectedIds.length === 0) return toast.error("Önce en az bir haber seçin");
    const label = nextActive ? "yayına al" : "yayından kaldır";
    if (!window.confirm(`Seçili ${selectedIds.length} haberi ${label}mak istediğinize emin misiniz?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/news/bulk-update-active", {
        ids: selectedIds,
        is_active: nextActive,
      });
      toast.success(`${r.data.updated} haber ${nextActive ? "yayına alındı" : "yayından kaldırıldı"}`);
      setSelectedIds([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toplu güncelleme başarısız");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div data-testid="admin-news-page">
      <AdminPageHeader
        title="Haber Yönetimi"
        subtitle={`Toplam ${total} haber${showDeleted ? " (çöp kutusu)" : showArchived ? " (arşiv dahil)" : ""}`}
        action={
          <button
            onClick={openNew}
            className="px-4 py-2 bg-[#e11d2a] text-white rounded-md text-sm font-bold flex items-center gap-1 hover:bg-[#c01825] shadow-sm"
            data-testid="admin-news-new-btn"
          >
            <Plus className="w-4 h-4" /> Yeni Haber
          </button>
        }
      />

      {/* Toolbar */}
      <div className="bg-white border border-gray-100 rounded-lg p-3 mb-3 flex flex-wrap gap-2 items-center sticky top-0 z-10">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Başlık ara..."
            className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-md text-sm"
            data-testid="admin-news-search"
          />
        </div>
        <label
          className={`inline-flex items-center gap-1.5 text-sm cursor-pointer px-3 py-2 border rounded-md hover:bg-gray-50 transition-colors ${
            globalSearch
              ? "border-[#e11d2a] bg-red-50 text-[#e11d2a] font-semibold"
              : "border-gray-200 bg-white"
          }`}
          data-testid="admin-news-global-search-toggle"
          title="İşaretli olduğunda arama seçili ili yok sayar ve tüm aktif haberlerde çalışır"
        >
          <input
            type="checkbox"
            checked={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.checked)}
            className="w-4 h-4 accent-[#e11d2a]"
          />
          <span>Tüm haberlerde ara</span>
        </label>
        <select
          value={filterProvince}
          onChange={(e) => setFilterProvince(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
        >
          <option value="">Tüm İller</option>
          <option value="null">Sadece Genel</option>
          {provinces.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
        >
          <option value="">Tüm Kategoriler</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer px-3 py-2 border border-gray-200 rounded-md bg-white hover:bg-gray-50" data-testid="admin-news-archive-toggle">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-4 h-4 accent-[#e11d2a]" />
          <span>Süresi geçenleri göster</span>
        </label>
        <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer px-3 py-2 border border-gray-200 rounded-md bg-white hover:bg-gray-50" data-testid="admin-news-deleted-toggle">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} className="w-4 h-4 accent-[#e11d2a]" />
          <span>Silinenler (Çöp Kutusu)</span>
        </label>
      </div>

      {showDeleted && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 flex items-center justify-between gap-3 flex-wrap" data-testid="admin-news-trash-banner">
          <div>
            🗑️ <strong>Çöp kutusu görünümü</strong> — silinen haberleri buradan geri yükleyebilirsiniz. Kalıcı silme yalnızca süper admin ile mümkündür.
          </div>
          {items.length > 0 && (
            <button
              type="button"
              onClick={restoreAll}
              disabled={bulkBusy}
              data-testid="admin-news-restore-all"
              className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              ↩ Hepsini Kurtar ({total})
            </button>
          )}
        </div>
      )}

      {/* Bulk actions bar (non-trash view) */}
      {!showDeleted && selectedIds.length > 0 && (
        <div
          className="mb-3 bg-red-50 border border-red-200 rounded p-3 text-sm flex items-center justify-between gap-3 flex-wrap"
          data-testid="admin-news-bulk-bar"
        >
          <div className="text-red-900">
            <strong>{selectedIds.length}</strong> haber seçildi
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={openBulkDate}
              disabled={bulkBusy}
              data-testid="admin-news-bulk-date-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#e11d2a] text-white rounded hover:bg-[#c01825] disabled:opacity-50"
            >
              <CalendarClock className="w-3.5 h-3.5" /> Yayın Tarihini Değiştir
            </button>
            <button
              type="button"
              onClick={() => applyBulkActive(true)}
              disabled={bulkBusy}
              data-testid="admin-news-bulk-activate"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              <Eye className="w-3.5 h-3.5" /> Yayına Al
            </button>
            <button
              type="button"
              onClick={() => applyBulkActive(false)}
              disabled={bulkBusy}
              data-testid="admin-news-bulk-deactivate"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-gray-700 text-white rounded hover:bg-gray-800 disabled:opacity-50"
            >
              <EyeOff className="w-3.5 h-3.5" /> Yayından Kaldır
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-gray-200 text-gray-700 rounded hover:bg-gray-50"
              data-testid="admin-news-bulk-clear"
            >
              Seçimi Temizle
            </button>
          </div>
        </div>
      )}

      {/* News list (full width) */}
      <div className="bg-white border border-gray-100 rounded-lg overflow-x-auto" data-testid="admin-news-table">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2 w-8">
                {!showDeleted && (
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#e11d2a] cursor-pointer"
                    checked={allOnPageSelected}
                    ref={(el) => { if (el) el.indeterminate = someOnPageSelected; }}
                    onChange={toggleAllOnPage}
                    aria-label="Tümünü seç"
                    data-testid="admin-news-select-all"
                  />
                )}
              </th>
              <th className="px-3 py-2 w-12">Foto</th>
              <th className="px-3 py-2">Başlık</th>
              <th className="px-3 py-2">İl</th>
              <th className="px-3 py-2">Kategori</th>
              <th className="px-3 py-2 text-center">Slider</th>
              <th className="px-3 py-2 text-center">Aktif</th>
              <th className="px-3 py-2 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((n) => {
              const selected = selectedIds.includes(n.id);
              return (
              <tr
                key={n.id}
                className={`border-t hover:bg-gray-50 ${selected ? "bg-red-50/40" : ""}`}
              >
                <td className="px-3 py-2">
                  {!n.deleted_at && !showDeleted && (
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-[#e11d2a] cursor-pointer"
                      checked={selected}
                      onChange={() => toggleOne(n.id)}
                      aria-label="Seç"
                      data-testid={`admin-news-select-${n.id}`}
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  {n.cover_image ? (
                    <img src={n.cover_image} alt="" className="w-12 h-9 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-9 bg-gray-100 rounded" />
                  )}
                </td>
                <td className="px-3 py-2 max-w-md">
                  <div className="font-semibold truncate flex items-center gap-1.5">
                    <span className={`truncate ${n.deleted_at ? "line-through text-gray-400" : ""}`}>{n.title}</span>
                    {n.deleted_at && (
                      <span className="text-[9px] uppercase font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded shrink-0">Silindi</span>
                    )}
                    {!n.deleted_at && n.expire_at && new Date(n.expire_at) < new Date() && (
                      <span className="text-[9px] uppercase font-bold bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded shrink-0">Arşiv</span>
                    )}
                  </div>
                  {n.deleted_at && (
                    <div className="text-[10px] text-red-500 mt-0.5">
                      Silinme: {new Date(n.deleted_at).toLocaleString("tr-TR")}
                    </div>
                  )}
                  {n.gallery?.length > 0 && (
                    <div className="text-[10px] text-gray-400 mt-0.5">+ {n.gallery.length} galeri foto</div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{provinces.find((p) => p.id === n.province_id)?.name || "Genel"}</td>
                <td className="px-3 py-2 text-xs">{cats.find((c) => c.id === n.category_id)?.name || "-"}</td>
                <td className="px-3 py-2 text-center">{n.is_in_slider ? "⭐" : ""}</td>
                <td className="px-3 py-2 text-center">{n.deleted_at ? "🗑️" : n.is_active ? "✅" : "❌"}</td>
                <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                  {n.deleted_at ? (
                    <>
                      <button
                        onClick={() => restore(n.id)}
                        className="text-emerald-600 text-xs hover:underline inline-flex items-center gap-1"
                        data-testid={`admin-news-restore-${n.id}`}
                      >
                        ↩ Geri Yükle
                      </button>
                      <button
                        onClick={() => hardRemove(n.id)}
                        className="text-red-600 text-xs hover:underline"
                        title="Kalıcı olarak sil (geri alınamaz)"
                        data-testid={`admin-news-hard-delete-${n.id}`}
                      >
                        Kalıcı Sil
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => openEdit(n)} className="text-blue-600 text-xs hover:underline inline-flex items-center gap-1" data-testid={`admin-news-edit-${n.id}`}>
                        <Pencil className="w-3 h-3" /> Düzenle
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ id: n.id, title: n.title })}
                        className="text-red-600 hover:text-red-700"
                        title="Sil"
                        data-testid={`admin-news-delete-${n.id}`}
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="8" className="p-8 text-center text-gray-500">Sonuç bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div
        className="mt-3 flex items-center justify-between gap-2 flex-wrap text-sm"
        data-testid="admin-news-pagination"
      >
        <div className="text-gray-500">
          Toplam <strong>{total}</strong> haber • Sayfa {page + 1} / {totalPages}
          {" "}(bu sayfada {items.length} kayıt)
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage(0)}
            disabled={page === 0}
            data-testid="admin-news-page-first"
            className="px-3 py-1.5 text-xs border rounded disabled:opacity-40"
          >
            « İlk
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            data-testid="admin-news-page-prev"
            className="px-3 py-1.5 text-xs border rounded disabled:opacity-40"
          >
            ‹ Önceki
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            data-testid="admin-news-page-next"
            className="px-3 py-1.5 text-xs border rounded disabled:opacity-40"
          >
            Sonraki ›
          </button>
          <button
            type="button"
            onClick={() => setPage(totalPages - 1)}
            disabled={page >= totalPages - 1}
            data-testid="admin-news-page-last"
            className="px-3 py-1.5 text-xs border rounded disabled:opacity-40"
          >
            Son »
          </button>
        </div>
      </div>

      {/* Delete confirmation modal — better than a bare window.confirm */}
      {deleteConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[200]"
            onClick={() => setDeleteConfirm(null)}
          />
          <div
            className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none"
            data-testid="admin-news-delete-modal"
          >
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full pointer-events-auto">
              <div className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">Haberi Sil</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      "<span className="font-semibold">{deleteConfirm.title}</span>" başlıklı haber{" "}
                      <strong>çöp kutusuna</strong> taşınacak. Kayıt hemen silinmez —
                      Silinenler sekmesinden dilediğinizde <strong>geri yükleyebilirsiniz</strong>.
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Kalıcı silme yalnızca süper admin tarafından çöp kutusundan yapılabilir.
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 border-t flex justify-end gap-2 bg-gray-50 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm border rounded hover:bg-white"
                  data-testid="admin-news-delete-modal-cancel"
                >
                  İptal
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const id = deleteConfirm.id;
                    setDeleteConfirm(null);
                    await remove(id);
                  }}
                  className="px-4 py-2 text-sm bg-[#e11d2a] text-white rounded hover:bg-[#c01825] flex items-center gap-1.5"
                  data-testid="admin-news-delete-modal-confirm"
                >
                  <Trash2 className="w-4 h-4" /> Çöp Kutusuna Taşı
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Slide-over editor panel */}
      {open && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[90]" onClick={close} />
          <aside
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[640px] bg-white z-[100] shadow-2xl flex flex-col"
            data-testid="admin-news-editor"
          >
            <header className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h3 className="font-black text-lg">{editing ? "Haberi Düzenle" : "Yeni Haber"}</h3>
                <p className="text-xs text-gray-500">Görseller medya havuzuna otomatik kaydedilir.</p>
              </div>
              <button onClick={close} className="p-1.5 hover:bg-gray-100 rounded" data-testid="admin-news-editor-close">
                <X className="w-5 h-5" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <Field
                label="Başlık *"
                value={f.title}
                onChange={(e) => setF({ ...f, title: e.target.value, slug: f.slug || slugify(e.target.value) })}
                data-testid="admin-news-title"
              />
              <Field
                label="Slug (URL)"
                value={f.slug}
                onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })}
              />
              <TextArea rows={2} label="Spot" value={f.spot} onChange={(e) => setF({ ...f, spot: e.target.value })} />
              <TextArea rows={8} label="İçerik *" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} />
              <p className="text-[11px] text-gray-500 -mt-2 leading-snug">
                💡 <strong>Video gömme:</strong> Bir YouTube veya Vimeo linkini <em>kendi satırına</em> yapıştırın
                (örn. <code>https://youtu.be/abc123</code>) — yayınlandığında otomatik olarak oynatıcı olarak yerleşir.
              </p>

              {/* Cover image */}
              <div className="border rounded-lg p-3 bg-gray-50">
                <MediaPicker
                  label="Kapak Görseli"
                  value={f.cover_image}
                  onChange={(url) => setF({ ...f, cover_image: url })}
                  scope={f.province_id ? "local" : "shared"}
                  province_id={f.province_id || null}
                  testId="news-cover-picker"
                />
              </div>

              {/* Gallery */}
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-600">Galeri (ek fotoğraflar)</label>
                  {f.gallery?.length > 0 && (
                    <span className="text-[10px] text-gray-500">{f.gallery.length} foto</span>
                  )}
                </div>
                <MediaPicker
                  multi
                  value={f.gallery}
                  onChange={(urls) => setF({ ...f, gallery: urls })}
                  scope={f.province_id ? "local" : "shared"}
                  province_id={f.province_id || null}
                  testId="news-gallery-picker"
                />
                {f.gallery?.length > 1 && (
                  <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-1">
                    {f.gallery.map((u, idx) => (
                      <div key={idx} className="flex items-center justify-center gap-1 text-[10px] text-gray-500">
                        <button type="button" onClick={() => moveGallery(idx, -1)} disabled={idx === 0} className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30">
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <span>#{idx + 1}</span>
                        <button type="button" onClick={() => moveGallery(idx, 1)} disabled={idx === f.gallery.length - 1} className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30">
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Select
                  label="İl"
                  value={f.province_id || ""}
                  options={[{ value: "", label: "Genel" }, ...provinces.map((p) => ({ value: p.id, label: p.name }))]}
                  onChange={(e) => setF({ ...f, province_id: e.target.value })}
                />
                <Select
                  label="Kategori"
                  value={f.category_id || ""}
                  options={[{ value: "", label: "-" }, ...cats.map((c) => ({ value: c.id, label: c.name }))]}
                  onChange={(e) => setF({ ...f, category_id: e.target.value })}
                />
              </div>

              {/* Publish & Expire dates */}
              <div className="grid grid-cols-2 gap-3 border-t pt-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-600">Yayın Tarihi</label>
                  <input
                    type="datetime-local"
                    value={f.publish_at ? f.publish_at.slice(0, 16) : ""}
                    onChange={(e) => setF({ ...f, publish_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                    className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                    data-testid="admin-news-publish-at"
                  />
                  <p className="text-[10px] text-gray-500 mt-0.5">Boş bırakılırsa hemen yayınlanır.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-600">Yayından Kaldırma Tarihi (Arşivle)</label>
                  <div className="flex gap-1">
                    <input
                      type="datetime-local"
                      value={f.expire_at ? f.expire_at.slice(0, 16) : ""}
                      onChange={(e) => setF({ ...f, expire_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm"
                      data-testid="admin-news-expire-at"
                    />
                    {f.expire_at && (
                      <button
                        type="button"
                        onClick={() => setF({ ...f, expire_at: "" })}
                        className="px-2 py-1.5 border rounded text-xs hover:bg-gray-50"
                        title="Süreyi temizle"
                      >
                        Temizle
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5">Bu tarihten sonra önyüzde görünmez, arşivde kalır.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <Toggle label="Slider'a ekle" value={f.is_in_slider} onChange={(v) => setF({ ...f, is_in_slider: v })} />
                <div>
                  <Field
                    label="Slider sırası (1 = ilk, boş/0 = en sona)"
                    type="number"
                    value={f.slider_priority}
                    onChange={(e) => setF({ ...f, slider_priority: +e.target.value })}
                    min={0}
                    max={9999}
                  />
                </div>
                <Toggle label="Öne çıkar" value={f.is_featured} onChange={(v) => setF({ ...f, is_featured: v })} />
                <Toggle label="Son Dakika" value={f.is_breaking} onChange={(v) => setF({ ...f, is_breaking: v })} />
                <Toggle label="Aktif" value={f.is_active} onChange={(v) => setF({ ...f, is_active: v })} />
              </div>

              {/* --- Kaynak (admin-only; public visitors never see these) --- */}
              <div className="pt-4 border-t space-y-2" data-testid="admin-news-source-section">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-700">Kaynak (Admin'e özel)</span>
                  <span className="text-[10px] text-gray-500">Zorunlu değildir. Önyüzde sadece admin görür.</span>
                </div>
                <Field
                  label="Kaynak (metin)"
                  value={f.source || ""}
                  onChange={(e) => setF({ ...f, source: e.target.value })}
                  placeholder="Örn. AA, DHA, Muhabirimiz vb."
                  data-testid="admin-news-source"
                />
                <Field
                  label="Kaynak URL (opsiyonel)"
                  type="url"
                  value={f.source_url || ""}
                  onChange={(e) => setF({ ...f, source_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="admin-news-source-url"
                />
              </div>

              {/* --- SEO / Arama Motoru Optimizasyonu --- */}
              <div className="pt-4 border-t space-y-3" data-testid="admin-news-seo-section">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wide text-[#e11d2a]">SEO</span>
                  <span className="text-xs text-gray-500">Arama motorunda nasıl görüneceğini belirler.</span>
                </div>
                <Field
                  label={`SEO Başlığı (${(f.seo_title || "").length}/60) — boş ise ana başlık kullanılır`}
                  value={f.seo_title || ""}
                  onChange={(e) => setF({ ...f, seo_title: e.target.value })}
                  placeholder={f.title || "Google'da görünecek başlık"}
                  maxLength={70}
                  data-testid="admin-news-seo-title"
                />
                <TextArea
                  rows={2}
                  label={`SEO Açıklaması (${(f.seo_description || "").length}/160) — boş ise spot kullanılır`}
                  value={f.seo_description || ""}
                  onChange={(e) => setF({ ...f, seo_description: e.target.value })}
                  placeholder={f.spot || "Arama sonuçlarında görünecek 1-2 cümle özet"}
                  maxLength={200}
                  data-testid="admin-news-seo-description"
                />
                <Field
                  label="Anahtar Kelimeler (virgülle ayrılmış)"
                  value={f.seo_keywords || ""}
                  onChange={(e) => setF({ ...f, seo_keywords: e.target.value })}
                  placeholder="düzce, gündem, belediye"
                  data-testid="admin-news-seo-keywords"
                />
                <Field
                  label="Odak Anahtar Kelime"
                  value={f.seo_focus_keyword || ""}
                  onChange={(e) => setF({ ...f, seo_focus_keyword: e.target.value })}
                  placeholder="Bu haberin ana konusu (örn. 'düzce depremi')"
                />
                <Field
                  label="Kanonik URL (opsiyonel)"
                  value={f.seo_canonical || ""}
                  onChange={(e) => setF({ ...f, seo_canonical: e.target.value })}
                  placeholder="https://sine81.com/haber/..."
                />
                <Toggle
                  label="Arama motorlarında gizle (noindex)"
                  value={!!f.seo_noindex}
                  onChange={(v) => setF({ ...f, seo_noindex: v })}
                  data-testid="admin-news-seo-noindex"
                />

                {/* Google preview */}
                <div className="border rounded-md p-3 bg-gray-50" data-testid="admin-news-seo-preview">
                  <div className="text-[10px] uppercase font-bold text-gray-500 mb-2">Google Önizlemesi</div>
                  <div className="text-[13px] text-emerald-800 truncate">
                    sine81.com › haber › {f.slug || "..."}
                  </div>
                  <div className="text-[18px] text-blue-800 leading-tight line-clamp-1 mt-0.5">
                    {(f.seo_title || f.title || "Haber başlığı") + " | SİNE81"}
                  </div>
                  <div className="text-[13px] text-gray-700 line-clamp-2 mt-0.5">
                    {(f.seo_description || f.spot || (f.content || "").replace(/<[^>]+>/g, "")).slice(0, 160) || "Açıklama..."}
                  </div>
                </div>
              </div>
            </div>

            <footer className="px-5 py-3 border-t flex gap-2 justify-end sticky bottom-0 bg-white">
              <button onClick={close} className="px-4 py-2 border rounded-md text-sm">İptal</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-5 py-2 bg-[#e11d2a] text-white rounded-md text-sm font-bold disabled:opacity-50"
                data-testid="admin-news-save"
              >
                {saving ? "Kaydediliyor..." : editing ? "Güncelle" : "Yayınla"}
              </button>
            </footer>
          </aside>
        </>
      )}

      {/* Bulk publish-date modal */}
      {bulkDateOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="admin-news-bulk-date-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setBulkDateOpen(false); }}
        >
          <div className="bg-white rounded-lg max-w-md w-full shadow-2xl">
            <header className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-black text-lg flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-[#e11d2a]" />
                Toplu Yayın Tarihi
              </h3>
              <button
                type="button"
                onClick={() => setBulkDateOpen(false)}
                className="p-1 hover:bg-gray-100 rounded"
                aria-label="Kapat"
              >
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="p-5 space-y-4 text-sm">
              <p className="text-gray-700">
                Seçilen <strong>{selectedIds.length}</strong> haberin yayın tarihi
                aşağıdaki tarihe güncellenecek.
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Yeni Yayın Tarihi
                </label>
                <input
                  type="datetime-local"
                  value={bulkPublishAt}
                  onChange={(e) => setBulkPublishAt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  data-testid="admin-news-bulk-date-input"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkClearExpire}
                  onChange={(e) => setBulkClearExpire(e.target.checked)}
                  className="w-4 h-4 accent-[#e11d2a] mt-0.5"
                  data-testid="admin-news-bulk-clear-expire"
                />
                <span className="text-xs text-gray-700">
                  <strong>Süre bitiş tarihini de temizle</strong> (arşive düşmüş haberler
                  tekrar yayına alınsın)
                </span>
              </label>
              <div className="bg-amber-50 border border-amber-200 text-amber-900 text-[11px] rounded p-2">
                Bu işlem geri alınamaz; her haberin yayın tarihi tek tek güncellenir.
              </div>
            </div>
            <footer className="px-5 py-3 border-t flex gap-2 justify-end">
              <button
                onClick={() => setBulkDateOpen(false)}
                className="px-4 py-2 border rounded-md text-sm"
                data-testid="admin-news-bulk-date-cancel"
              >
                İptal
              </button>
              <button
                onClick={applyBulkDate}
                disabled={bulkBusy || !bulkPublishAt}
                data-testid="admin-news-bulk-date-apply"
                className="px-5 py-2 bg-[#e11d2a] text-white rounded-md text-sm font-bold disabled:opacity-50"
              >
                {bulkBusy ? "Uygulanıyor..." : "Uygula"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
